import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { renderEditorCleanupBlock } from '@/lib/editorSaveMarker';
import type { AnchorData, PendingComment } from '@/store/reducers/types';

export type { AnchorData, PendingComment };

interface Props {
  pages: { title: string }[];
  activePageIndex: number;
  comments: PendingComment[];
  onAdd: (comment: PendingComment) => void;
  onRemove: (index: number) => void;
  onClickComment?: (comment: PendingComment) => void;
}

export function CommentComposer(props: Props) {
  const [draft, setDraft] = useState('');
  const [tagPage, setTagPage] = useState(true);

  const add = () => {
    const body = draft.trim();
    if (!body) return;
    const pageTitle =
      tagPage && props.pages[props.activePageIndex]
        ? props.pages[props.activePageIndex].title
        : undefined;
    props.onAdd({ body, pageTitle });
    setDraft('');
  };

  return (
    <div className="border-t p-3 space-y-2">
      {props.comments.length > 0 && (
        <ul className="space-y-1 max-h-40 overflow-y-auto">
          {props.comments.map((c, i) => {
            const isEditable = !c.editorSave && !!c.anchorId && !!c.anchorData;
            return (
              <li
                key={i}
                className={`flex items-start gap-2 text-sm bg-muted rounded p-2${isEditable ? ' cursor-pointer hover:bg-muted/80' : ''}`}
                onClick={isEditable ? () => props.onClickComment?.(c) : undefined}
                title={isEditable ? 'Click to edit this comment' : undefined}
              >
                <div className="flex-1">
                  {c.editorSave ? (
                    <>
                      <div className="text-xs text-muted-foreground">Committed edit</div>
                      <div className="font-mono text-xs">{c.editorSave.filePath}</div>
                      <div className="text-xs text-muted-foreground">
                        Commit <code>{c.editorSave.shortSha}</code> — will be cleaned up on submit
                      </div>
                    </>
                  ) : (
                    <>
                      {c.pageTitle && (
                        <div className="text-xs text-muted-foreground">
                          On page "{c.pageTitle}"
                        </div>
                      )}
                      <div>{c.body}</div>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); props.onRemove(i); }}
                  aria-label="Remove comment"
                  className="text-muted-foreground hover:text-foreground text-xs"
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Type a comment about this page or the article overall…"
        rows={3}
      />
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs flex items-center gap-1">
          <input
            type="checkbox"
            checked={tagPage}
            onChange={(e) => setTagPage(e.target.checked)}
          />
          Tag with current page
        </label>
        <Button size="sm" onClick={add} disabled={!draft.trim()}>
          + Add comment
        </Button>
      </div>
    </div>
  );
}

export function renderReviewBody(comments: PendingComment[]): string {
  const text = comments.filter((c) => !c.editorSave);
  const saves = comments.filter((c) => c.editorSave);

  const textPart = text
    .map((c) => c.pageTitle
      ? `- On page "${c.pageTitle}": ${c.body}`
      : `- ${c.body}`)
    .join('\n');

  const savesPart = saves
    .map((c) => renderEditorCleanupBlock(c.editorSave!))
    .join('\n\n');

  if (!textPart) return savesPart;
  if (!savesPart) return textPart;
  return `${textPart}\n\n${savesPart}`;
}
